import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShoppingBag, Calendar, RefreshCcw, X, CheckCircle2, Clock, XCircle, AlertCircle } from "lucide-react";
import { useAdminAuth, formatApiErrorDetail } from "../../lib/adminAuth";

const STATUS_META = {
  paid:      { label: "Ödendi",    color: "bg-sage-100 text-sage-700",         icon: CheckCircle2 },
  pending:   { label: "Bekliyor",  color: "bg-cream-200 text-ink/60",          icon: Clock },
  failed:    { label: "Başarısız", color: "bg-terracotta-50 text-terracotta-500", icon: XCircle },
  cancelled: { label: "İptal",     color: "bg-cream-300 text-ink/50",          icon: AlertCircle },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-medium px-2.5 py-1 rounded-full ${meta.color}`}>
      <Icon className="w-3 h-3" strokeWidth={1.5} />
      {meta.label}
    </span>
  );
}

export default function AdminOrders() {
  const { authedClient } = useAdminAuth();
  const [data, setData] = useState({ items: [], total_paid: 0, total_revenue_tl: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const { data: payload } = await authedClient.get("/admin/orders", { params });
      setData(payload);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [authedClient, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const cancelOrder = async (order) => {
    if (!window.confirm(`${order.buyer_name} adlı kişinin siparişini iptal etmek ve iade başlatmak istiyor musunuz?`)) return;
    setCancelling(true);
    try {
      await authedClient.post(`/admin/orders/${order.id}/cancel`);
      toast.success("Sipariş iptal edildi, iyzico iade başlatıldı");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İptal başarısız");
    } finally {
      setCancelling(false);
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Sipariş No", "Etkinlik", "Ad Soyad", "E-posta", "Telefon", "Tutar (TL)", "Durum", "Tarih"],
      ...data.items.map((o) => [
        o.id,
        o.event_title?.tr || o.event_title?.en || "",
        `${o.buyer_name} ${o.buyer_surname}`,
        o.buyer_email,
        o.buyer_phone || "",
        ((o.amount_kurus || 0) / 100).toFixed(2),
        o.status,
        o.created_at,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "intouch-orders.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-orders-page">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-7">
        <div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Siparişler</h1>
          <p className="mt-1 text-sm text-ink/60">
            <strong>{data.total_paid}</strong> tamamlanan ödeme ·{" "}
            <strong className="text-sage-700">{data.total_revenue_tl?.toLocaleString("tr-TR")} TL</strong> toplam gelir
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} disabled={!data.items.length} className="text-xs px-4 py-2 rounded-full border border-ink/15 hover:bg-cream-200 disabled:opacity-40">
            CSV indir
          </button>
          <button onClick={load} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink/60 hover:text-ink">
            <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
            Yenile
          </button>
        </div>
      </header>

      {/* Filtreler */}
      <div className="flex flex-wrap gap-2 mb-6">
        {["", "paid", "pending", "failed", "cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs uppercase tracking-[0.22em] px-4 py-2 rounded-full border transition-colors ${
              statusFilter === s ? "bg-ink text-cream-100 border-ink" : "border-ink/15 text-ink/65 hover:text-ink"
            }`}
          >
            {s === "" ? "Tümü" : STATUS_META[s]?.label || s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-ink/55 text-sm py-12 text-center">Yükleniyor…</div>
      ) : data.items.length === 0 ? (
        <div className="surface-card p-10 text-center text-ink/60">
          <ShoppingBag className="w-8 h-8 mx-auto mb-3 text-ink/30" strokeWidth={1.4} />
          Henüz sipariş yok.
        </div>
      ) : (
        <ul className="space-y-2.5">
          {data.items.map((o) => (
            <li key={o.id}>
              <button
                onClick={() => setSelected(o)}
                className="w-full text-left surface-card px-5 py-4 hover:shadow-soft transition-all"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <StatusBadge status={o.status} />
                    <span className="font-medium text-ink truncate">{o.buyer_name} {o.buyer_surname}</span>
                    <span className="text-ink/45 text-sm truncate hidden md:inline">· {o.buyer_email}</span>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="font-medium text-terracotta-400">{((o.amount_kurus || 0) / 100).toFixed(2)} TL</span>
                    <span className="text-xs text-ink/45 flex items-center gap-1">
                      <Calendar className="w-3 h-3" strokeWidth={1.5} />
                      {new Date(o.created_at).toLocaleDateString("tr-TR")}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-ink/60 mt-1 truncate">
                  {o.event_title?.tr || o.event_title?.en}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Detay modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-ink/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="surface-card w-full max-w-lg p-7 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <StatusBadge status={selected.status} />
                <h3 className="font-serif text-2xl mt-2">{selected.buyer_name} {selected.buyer_surname}</h3>
                <p className="text-sm text-ink/55">{selected.buyer_email}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-ink/40 hover:text-ink text-xl">×</button>
            </div>

            <dl className="space-y-3 text-sm mb-6">
              {[
                ["Sipariş No", <span className="font-mono text-xs">{selected.id}</span>],
                ["Etkinlik", selected.event_title?.tr || selected.event_title?.en],
                ["Tutar", `${((selected.amount_kurus || 0) / 100).toFixed(2)} TL`],
                ["Telefon", selected.buyer_phone || "—"],
                ["Katılımcı", selected.guests],
                ["Notlar", selected.notes || "—"],
                ["iyzico Token", <span className="font-mono text-xs break-all">{selected.iyzico_token || "—"}</span>],
                ["iyzico Payment ID", <span className="font-mono text-xs">{selected.iyzico_payment_id || "—"}</span>],
                ["Ödeme Durumu", selected.iyzico_payment_status || "—"],
                ["Oluşturulma", new Date(selected.created_at).toLocaleString("tr-TR")],
                ["Ödeme Tarihi", selected.paid_at ? new Date(selected.paid_at).toLocaleString("tr-TR") : "—"],
              ].map(([label, value]) => (
                <div key={label} className="flex gap-3">
                  <dt className="text-xs uppercase tracking-widest text-ink/45 w-32 shrink-0 pt-0.5">{label}</dt>
                  <dd className="text-ink/80">{value}</dd>
                </div>
              ))}
            </dl>

            {selected.status === "paid" && (
              <button
                onClick={() => cancelOrder(selected)}
                disabled={cancelling}
                className="w-full text-center rounded-xl border border-terracotta-200 text-terracotta-500 py-2.5 text-sm hover:bg-terracotta-50 disabled:opacity-50"
              >
                {cancelling ? "İptal ediliyor…" : "İptal et & İade başlat"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
